/* ==========================================================================
   Petpeps · Mobile slider  <pp-slider>
   Progressive enhancement on top of native scroll-snap. No dependencies.

   Markup:
   <pp-slider data-autoplay="0" data-loop="false">
     <ul data-pp-track> ...slides... </ul>
     <div class="pps-slider__nav" data-pp-nav>
       <button data-pp-prev></button>
       <div data-pp-dots></div> | <div data-pp-progress><span></span></div> | <span data-pp-counter></span>
       <button data-pp-next></button>
     </div>
   </pp-slider>

   Only activates while the track actually overflows, so desktop grids
   are untouched. Nav visibility per breakpoint is handled in CSS.
   ========================================================================== */
(function () {
  if (customElements.get('pp-slider')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class PPSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pp-track]');
      if (!this.track) return;
      this.nav = this.querySelector('[data-pp-nav]');
      this.prevBtn = this.querySelector('[data-pp-prev]');
      this.nextBtn = this.querySelector('[data-pp-next]');
      this.dotsEl = this.querySelector('[data-pp-dots]');
      this.progressEl = this.querySelector('[data-pp-progress] span');
      this.counterEl = this.querySelector('[data-pp-counter]');
      this.loop = this.dataset.loop === 'true';
      this.autoplayMs = parseInt(this.dataset.autoplay || '0', 10) * 1000;
      this.positions = [];
      this.index = 0;
      this.active = false;
      this.paused = false;

      this._onScroll = this.onScroll.bind(this);
      this._measure = this.measure.bind(this);

      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      // Pause autoplay while the user interacts
      ['pointerdown', 'touchstart', 'focusin', 'mouseenter'].forEach((ev) =>
        this.addEventListener(ev, () => { this.paused = true; }, { passive: true })
      );
      ['mouseleave', 'focusout'].forEach((ev) =>
        this.addEventListener(ev, () => { this.paused = false; })
      );
      this._onVisibility = () => { this.paused = document.hidden; };
      document.addEventListener('visibilitychange', this._onVisibility);

      this.ro = new ResizeObserver(this._measure);
      this.ro.observe(this.track);

      // Theme editor: jump to the selected block
      this._onBlockSelect = (e) => {
        var slide = this.track.querySelector('[data-block-id="' + e.detail.blockId + '"]');
        if (!slide || !this.active) return;
        var i = Array.prototype.indexOf.call(this.track.children, slide);
        this.goTo(this.slideToPosition(i), false);
        this.paused = true;
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', () => { this.paused = false; });

      this.measure();
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this._onScroll);
      if (this.ro) this.ro.disconnect();
      document.removeEventListener('visibilitychange', this._onVisibility);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      this.stopAutoplay();
    }

    get slides() {
      return Array.prototype.filter.call(this.track.children, function (el) {
        return el.offsetParent !== null || el.getClientRects().length;
      });
    }

    measure() {
      var t = this.track;
      var overflow = t.scrollWidth - t.clientWidth > 2;
      this.active = overflow;
      this.toggleAttribute('data-active', overflow);
      if (this.nav) this.nav.hidden = !overflow;
      if (!overflow) { this.stopAutoplay(); return; }

      // Reachable snap positions (the last few slides can't all snap to start)
      var padLeft = parseFloat(getComputedStyle(t).scrollPaddingLeft) || 0;
      var max = t.scrollWidth - t.clientWidth;
      var seen = [];
      this.slides.forEach(function (slide) {
        var pos = Math.min(Math.max(slide.offsetLeft - padLeft, 0), max);
        if (!seen.some(function (p) { return Math.abs(p - pos) < 4; })) seen.push(pos);
      });
      this.positions = seen;
      this.buildDots();
      this.update();
      this.startAutoplay();
    }

    slideToPosition(slideIndex) {
      var slide = this.slides[slideIndex];
      if (!slide) return 0;
      var padLeft = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      var target = slide.offsetLeft - padLeft;
      var best = 0;
      this.positions.forEach(function (p, i) {
        if (Math.abs(p - target) < Math.abs(this.positions[best] - target)) best = i;
      }, this);
      return best;
    }

    buildDots() {
      if (!this.dotsEl) return;
      var n = this.positions.length;
      if (this.dotsEl.children.length === n) return;
      this.dotsEl.innerHTML = '';
      for (var i = 0; i < n; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'pps-slider__dot';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + n);
        b.addEventListener('click', this.goTo.bind(this, i, true));
        this.dotsEl.appendChild(b);
      }
    }

    onScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.update();
      });
    }

    update() {
      var t = this.track;
      var x = t.scrollLeft;
      var idx = 0;
      this.positions.forEach(function (p, i) {
        if (Math.abs(p - x) < Math.abs(this.positions[idx] - x)) idx = i;
      }, this);
      this.index = idx;
      var n = this.positions.length;

      if (this.dotsEl) {
        Array.prototype.forEach.call(this.dotsEl.children, function (d, i) {
          d.classList.toggle('is-active', i === idx);
          if (i === idx) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
        });
      }
      if (this.counterEl) this.counterEl.textContent = (idx + 1) + ' / ' + n;
      if (this.progressEl) {
        var max = t.scrollWidth - t.clientWidth;
        var size = Math.max(t.clientWidth / t.scrollWidth, 0.08);
        var ratio = max > 0 ? x / max : 0;
        this.progressEl.style.width = (size * 100) + '%';
        this.progressEl.style.transform = 'translateX(' + (ratio * (1 - size) / size * 100) + '%)';
      }
      if (this.prevBtn) this.prevBtn.disabled = !this.loop && idx === 0;
      if (this.nextBtn) this.nextBtn.disabled = !this.loop && idx === n - 1;
    }

    goTo(i, userAction) {
      var n = this.positions.length;
      if (!n) return;
      if (i < 0) i = this.loop ? n - 1 : 0;
      if (i > n - 1) i = this.loop ? 0 : n - 1;
      this.track.scrollTo({
        left: this.positions[i],
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
      if (userAction) this.restartAutoplay();
    }

    step(dir) {
      this.goTo(this.index + dir, true);
    }

    startAutoplay() {
      if (!this.autoplayMs || this._timer || reduceMotion.matches) return;
      this._timer = setInterval(() => {
        if (this.paused || !this.active) return;
        var n = this.positions.length;
        this.goTo(this.index + 1 > n - 1 ? 0 : this.index + 1, false);
      }, this.autoplayMs);
    }

    stopAutoplay() {
      clearInterval(this._timer);
      this._timer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('pp-slider', PPSlider);
})();